import React, { useEffect, useState } from "react";
import { ActionHandlerFunction, PaginationItem } from "@preveil-api";
import { DirectoryEntity, DriveUIActionTypes, Message, MessageHandlerDisplayType, MessageToastTypes, useAppDispatch, TrashConfirmationMessages } from "src/common";
import { Icon, Pagination, PaginationDisplay, ToolbarButton, ToolbarWrapper, ToolbarFilter } from "src/components";
import { uiActions } from "src/store";

type AllProps = {
  is_loading: boolean;
  handleAction: ActionHandlerFunction;
  pagination?: PaginationItem;
  selected: string[];
  directory?: DirectoryEntity;
  total: number;
};

function TrashToolbarComponent(props: AllProps) {
  const { is_loading, handleAction, pagination, selected, directory, total } = props;
  const dispatch = useAppDispatch();
  const [btn_disabled, setButtonDisabled] = useState<boolean>(selected.length <= 0);
  const [filter_param, setFilterParam] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(false);

  // Description: sets disabled to true if the total rows are 0. (for disabling filter and empty trash)
  useEffect(() => {
    const not_filtering = !directory && total === 0 || !!directory && directory.entries.length === 0;
    !!pagination && setDisabled(pagination.totalRows <= 0 && not_filtering);
  }, [pagination]);

  // Description: Bulk delete and bulk restore is disabled if no items are selected
  useEffect(() => {
    (selected.length <= 0) ? setButtonDisabled(true) : setButtonDisabled(false);
  }, [selected]);

  // Description: Confirmation modal for emptying trash
  function confirmEmptyTrash() {
    const confirmation_dialog = new Message(TrashConfirmationMessages.empty_trash_message,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary, TrashConfirmationMessages.empty_trash_title,
      {
        label: "Yes",
        data: true,
        action: () => handleAction({ actionType: DriveUIActionTypes.EmptyTrash }),
      },
      { label: "No" },
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  return (
    <ToolbarWrapper
      leftContent={<ToolbarFilter
        disabled={disabled}
        filter_param={filter_param}
        onChange={(value) => { setFilterParam(value); handleAction({ actionType: DriveUIActionTypes.Filter, params: value }); }}
        onClick={() => { setFilterParam(""); handleAction({ actionType: DriveUIActionTypes.Filter, params: "" }); }} />}
      pagination={!!pagination ? <Pagination pagination={pagination} handleAction={handleAction} /> : <></>}
      paginationInfo={!!pagination ? <PaginationDisplay pagination={pagination} /> : <></>}
      refreshAction={() => handleAction({ actionType: DriveUIActionTypes.Refresh, params: !directory })}
      refreshTooltipContent="Refresh Drive Trash"
      isLoading={is_loading}
    >
      {!directory ? <>
        <ToolbarButton
          disabled={btn_disabled}
          onClick={() => handleAction({ actionType: DriveUIActionTypes.RestoreItems })}
          ariaLabel="Restore Selected"
          tooltipContent="Restore Selected Items">
          <Icon className="pv-icon-move" />
        </ToolbarButton>
        <ToolbarButton
          disabled={btn_disabled}
          onClick={() => handleAction({ actionType: DriveUIActionTypes.Delete })}
          ariaLabel="Delete Selected"
          tooltipContent="Delete Selected Items">
          <Icon className="ficon-trash-2" />
        </ToolbarButton>
        <ToolbarButton
          disabled={disabled}
          onClick={() => confirmEmptyTrash()}
          ariaLabel="Empty Trash"
          tooltipContent="Empty Trash">
          <Icon className="ficon-archive" />
        </ToolbarButton>
      </> : <></>}
    </ToolbarWrapper>
  );
}

export default React.memo(TrashToolbarComponent);
